<template>
  <div class="toggle-section">
    <div class="container mt-5">
      <section class="text-center dark-grey-text">
        <h2 class="quantum-title font-weight-bold mb-4 pb-2">
          Nos formations<br /><span class="quantum">taxi & vtc</span>
        </h2>

        <div class="container-fluid formations-menu-shell">
          <section
            class="formations-menu pop"
            aria-label="Choix des formations"
          >
            <button
              type="button"
              class="formations-menu__paddle formations-menu__paddle--left"
              aria-label="Choix precedent"
              @click="cycleSelection(-1)"
            >
              <span class="formations-menu__paddle-mark">-</span>
              <span class="formations-menu__paddle-text">Shift</span>
            </button>

            <div
              class="formations-menu__cluster"
              @mouseleave="resetPreview"
              @focusout="handleClusterFocusOut"
            >
              <div class="formations-menu__stack formations-menu__stack--left">
                <button
                  v-for="item in leftItems"
                  :key="item.id"
                  type="button"
                  class="formations-menu__card"
                  :class="[
                    `formations-menu__card--${item.tone}`,
                    { 'is-active': displayedItemId === item.id },
                  ]"
                  :aria-label="item.label"
                  @click="toggleItem(item.id)"
                  @mouseenter="previewItem(item.id)"
                  @focus="previewItem(item.id)"
                >
                  <span class="formations-menu__icon" aria-hidden="true">
                    <i :class="item.icon"></i>
                  </span>
                  <span class="formations-menu__label">
                    <strong>{{ item.title }}</strong>
                    <span>{{ item.subtitle }}</span>
                  </span>
                </button>
              </div>

              <button
                type="button"
                class="formations-menu__center"
                :class="{ 'is-idle': !displayedItemId }"
                :style="centerStyle"
                :aria-label="centerAriaLabel"
                @click="handleCenterClick"
                @mouseenter="resetPreview"
                @focus="resetPreview"
              >
                <span
                  class="formations-menu__dial-track"
                  aria-hidden="true"
                ></span>
                <span
                  v-for="tick in gaugeTicks"
                  :key="tick.id"
                  class="formations-menu__dial-tick"
                  :class="{ 'is-hot': displayedItemId && tick.hot }"
                  :style="{
                    transform: `translateX(-50%) rotate(${tick.angle}deg)`,
                  }"
                  aria-hidden="true"
                ></span>
                <span class="formations-menu__dial-needle" aria-hidden="true">
                  <span class="formations-menu__dial-needle-arm"></span>
                  <span class="formations-menu__dial-cap"></span>
                </span>
                <span class="formations-menu__dial-readout" aria-hidden="true">
                  <strong>{{ displayedGauge.label }}</strong>
                  <span>{{ displayedGauge.detail }}</span>
                </span>
                <span
                  v-if="displayedGauge.gear"
                  class="formations-menu__dial-gear"
                  aria-hidden="true"
                >
                  {{ displayedGauge.gear }}
                </span>
              </button>

              <div class="formations-menu__stack formations-menu__stack--right">
                <button
                  v-for="item in rightItems"
                  :key="item.id"
                  type="button"
                  class="formations-menu__card"
                  :class="[
                    `formations-menu__card--${item.tone}`,
                    { 'is-active': displayedItemId === item.id },
                  ]"
                  :aria-label="item.label"
                  @click="toggleItem(item.id)"
                  @mouseenter="previewItem(item.id)"
                  @focus="previewItem(item.id)"
                >
                  <span class="formations-menu__icon" aria-hidden="true">
                    <i :class="item.icon"></i>
                  </span>
                  <span class="formations-menu__label">
                    <strong>{{ item.title }}</strong>
                    <span>{{ item.subtitle }}</span>
                  </span>
                </button>
              </div>
            </div>

            <button
              type="button"
              class="formations-menu__paddle formations-menu__paddle--right"
              aria-label="Choix suivant"
              @click="cycleSelection(1)"
            >
              <span class="formations-menu__paddle-mark">+</span>
              <span class="formations-menu__paddle-text">Shift</span>
            </button>
          </section>
        </div>
      </section>
    </div>

    <div class="toggle-content">
      <section v-if="!activeItem" class="toggle-overview">
        <div class="toggle-overview__hero fall">
          <p class="toggle-overview__eyebrow">Présentation générale</p>
          <h3 class="toggle-overview__title">
            Préparez votre projet taxi ou VTC avant de choisir votre formule.
          </h3>
          <p class="toggle-overview__copy">
            La page rassemble toutes les offres de l'institut: formation taxi,
            formation VTC, formats modulables et mobilité. Appuyez sur
            <strong>Start</strong> pour ouvrir directement la formation taxi, ou
            utilisez <strong>Shift -</strong> et <strong>Shift +</strong>
            pour faire défiler les parcours.
          </p>
        </div>

        <div class="toggle-overview__chips fall">
          <button
            v-for="item in items"
            :key="`${item.id}-chip`"
            type="button"
            :class="[
              'toggle-overview__chip',
              { 'toggle-overview__chip--active': activeItem === item.id },
            ]"
            @click="toggleItem(item.id)"
          >
            {{ item.chipLabel }}
          </button>
        </div>

        <div class="toggle-overview__blocks">
          <ServiceFeatureBlock
            title="Repérez rapidement le parcours qui correspond à votre objectif"
            tone="secondary"
            class="slide-left"
            :copy-html="overviewCopy.intro"
          >
            <template #media>
              <img
                src="@/assets/pictures/itti-hall.webp"
                alt="vehicule taxi et voiture vtc"
                class="img-fluid"
                loading="lazy"
              />
            </template>
          </ServiceFeatureBlock>
          <div class="hr-d"></div>
          <ServiceFeatureBlock
            title="Comparez le rythme, le financement et l'accompagnement"
            tone="primary"
            reverse
            class="slide-right"
            :copy-html="overviewCopy.support"
          >
            <template #media>
              <img
                src="@/assets/pictures/salle-formation-taxi-vtc.webp"
                alt="salle de formation taxi et vtc"
                class="img-fluid"
                loading="lazy"
              />
            </template>
          </ServiceFeatureBlock>
        </div>
      </section>

      <div
        class="toggle-item"
        :class="{ active: activeItem === 'formation-taxi' }"
      >
        <ServicesTaxi />
      </div>
      <div
        class="toggle-item"
        :class="{ active: activeItem === 'formation-vtc' }"
      >
        <ServicesVtc />
      </div>
      <div
        class="toggle-item"
        :class="{ active: activeItem === 'formations-acceleree-vtc-taxi' }"
      >
        <ServicesAccelere />
      </div>
      <div
        class="toggle-item"
        :class="{ active: activeItem === 'carte-pro-vtc-taxi' }"
      >
        <ServicesMobilite />
      </div>
    </div>
  </div>
</template>

<script>
import ServicesTaxi from "@/components/services/ServicesTaxi.vue";
import ServicesVtc from "@/components/services/ServicesVtc.vue";
import ServicesAccelere from "@/components/services/ServicesAccelere.vue";
import ServicesMobilite from "@/components/services/ServicesMobilite.vue";
import ServiceFeatureBlock from "@/components/services/ServiceFeatureBlock.vue";
import {
  getServicesItemIdFromRoute,
  getServicesRouteLocation,
} from "@/components/services/servicesSeoData";

const overviewCopyData = {
  intro:
    "L'Institut Taxi regroupe ici les <strong>formations taxi</strong>, les <strong>formations VTC</strong>, les formats <strong>modulables</strong> et les solutions de <strong>mobilité</strong>. Le menu permet d'ouvrir chaque univers sans perdre la vue d'ensemble, pour comparer plus simplement les parcours avant de consulter les prix et le détail de chaque formule.",
  support:
    "Chaque parcours propose un cadre pédagogique clair avec préparation à l'examen, accompagnement administratif et solutions de financement. Vous pouvez ensuite choisir une session en journée, en soirée, en week-end ou une passerelle selon votre disponibilité, votre niveau d'avancement et votre objectif professionnel.",
};

export default {
  name: "ServicesToggle",
  components: {
    ServicesTaxi,
    ServicesVtc,
    ServicesAccelere,
    ServicesMobilite,
    ServiceFeatureBlock,
  },
  data() {
    return {
      activeItem: null,
      previewItemId: null,
      overviewCopy: overviewCopyData,
      idleGauge: {
        label: "START",
        detail: "Moins shift et plus",
        angle: 0,
        gear: "",
      },
      items: [
        {
          id: "formation-taxi",
          title: "Formations",
          subtitle: "taxi",
          label: "Formation taxi",
          chipLabel: "Taxi",
          tone: "taxi",
          icon: "fa fa-taxi",
          gauge: {
            gear: "1",
            label: "TAXI",
            detail: "formation",
            angle: -112,
          },
        },
        {
          id: "formation-vtc",
          title: "Formations",
          subtitle: "VTC",
          label: "Formation VTC",
          chipLabel: "VTC",
          tone: "vtc",
          icon: "fa fa-car",
          gauge: {
            gear: "2",
            label: "VTC",
            detail: "chauffeur",
            angle: -42,
          },
        },
        {
          id: "formations-acceleree-vtc-taxi",
          title: "Formations",
          subtitle: "modulables",
          label: "Formation modulable",
          chipLabel: "Modulable",
          tone: "modulable",
          icon: "fa fa-sliders",
          gauge: {
            gear: "3",
            label: "FLEX",
            detail: "modulable",
            angle: 24,
          },
        },
        {
          id: "carte-pro-vtc-taxi",
          title: "Formation",
          subtitle: "mobilite",
          label: "Formation mobilite",
          chipLabel: "Mobilité",
          tone: "mobilite",
          icon: "fa fa-map-marker",
          gauge: {
            gear: "4",
            label: "MOBI",
            detail: "mobilite",
            angle: 88,
          },
        },
      ],
    };
  },
  computed: {
    leftItems() {
      return this.items.slice(0, 2);
    },
    rightItems() {
      return this.items.slice(2);
    },
    displayedItemId() {
      return this.previewItemId || this.activeItem;
    },
    displayedItem() {
      return (
        this.items.find((item) => item.id === this.displayedItemId) || null
      );
    },
    displayedGauge() {
      return this.displayedItem ? this.displayedItem.gauge : this.idleGauge;
    },
    centerStyle() {
      return {
        "--needle-angle": `${this.displayedGauge.angle}deg`,
      };
    },
    centerAriaLabel() {
      return this.displayedItem ? this.displayedItem.label : "Start";
    },
    gaugeTicks() {
      return [
        { id: 1, angle: -118, hot: false },
        { id: 2, angle: -88, hot: false },
        { id: 3, angle: -58, hot: false },
        { id: 4, angle: -28, hot: false },
        { id: 5, angle: 2, hot: false },
        { id: 6, angle: 32, hot: true },
        { id: 7, angle: 62, hot: true },
        { id: 8, angle: 92, hot: true },
      ];
    },
  },
  watch: {
    "$route.params.section"() {
      this.syncFromRoute(this.$route);
    },
  },
  mounted() {
    this.syncFromRoute(this.$route);
    import("@/assets/globalJs/global.js").then((module) => {
      const { observeElements } = module;
      observeElements(".pop", ".fall", ".slide-left", ".slide-right");
    });
  },
  methods: {
    resolveItemId(itemId) {
      return this.items.some((item) => item.id === itemId) ? itemId : null;
    },
    syncFromRoute(route) {
      this.activeItem = this.resolveItemId(getServicesItemIdFromRoute(route));
      this.previewItemId = null;
    },
    toggleItem(itemId, { scroll = true } = {}) {
      const resolvedItemId = this.resolveItemId(itemId);
      if (!resolvedItemId) {
        return;
      }

      this.activeItem = resolvedItemId;
      this.previewItemId = null;
      this.syncQueryWithSelection(resolvedItemId);

      if (scroll) {
        this.$nextTick(() => {
          this.scrollToSelection();
        });
      }
    },
    previewItem(itemId) {
      const resolvedItemId = this.resolveItemId(itemId);
      this.previewItemId = resolvedItemId;
    },
    resetPreview() {
      this.previewItemId = null;
    },
    handleCenterClick() {
      if (!this.displayedItemId) {
        this.toggleItem("formation-taxi");
        return;
      }

      this.toggleItem(this.displayedItemId);
    },
    handleClusterFocusOut(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.resetPreview();
      }
    },
    cycleSelection(direction) {
      if (!this.activeItem) {
        const initialIndex = direction < 0 ? this.items.length - 1 : 0;
        this.toggleItem(this.items[initialIndex].id);
        return;
      }

      const currentIndex = this.items.findIndex(
        (item) => item.id === this.activeItem
      );
      const nextIndex =
        (currentIndex + direction + this.items.length) % this.items.length;
      this.toggleItem(this.items[nextIndex].id);
    },
    scrollToSelection() {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;

      if (isMobile) {
        const targetElement = document.getElementById("item-1_ou");
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop,
            behavior: "smooth",
          });
        }
        return;
      }

      const anchorElement = document.querySelector("#formations-taxi-vtc");
      if (anchorElement) {
        anchorElement.scrollIntoView({ behavior: "smooth" });
      }
    },
    syncQueryWithSelection(itemId) {
      const currentItemId = getServicesItemIdFromRoute(this.$route);
      if (currentItemId === itemId) {
        return;
      }

      this.$router.replace(getServicesRouteLocation(itemId)).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.quantum-title {
  font-size: clamp(1.75rem, 1.35rem + 1vw, 2.35rem);
  font-family: "adam";
  line-height: 1.06;

  & span {
    font-size: 1.18em;
  }
}

.toggle-section {
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
}

.formations-menu-shell {
  padding: 0;
}

.formations-menu {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 0;
  width: min(100%, 420px);
  margin: 18px auto 12px;
  padding: 0 0 18px;
}

.formations-menu__cluster {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  min-height: 172px;
  overflow: visible;
  filter: drop-shadow(0 12px 22px rgba(0, 42, 84, 0.1));
}

.formations-menu__stack {
  display: grid;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 0;
  min-width: 0;

  &--left {
    border-right: 1px solid rgba(255, 255, 255, 0.12);
  }

  .formations-menu__card:first-child {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
}

.formations-menu__card {
  --card-active-background: linear-gradient(145deg, #2b3138 0%, #12171d 100%);
  --card-active-color: #f7fafc;
  --card-active-icon: currentColor;
  --card-active-ring: rgba(255, 255, 255, 0.34);
  position: relative;
  z-index: 1;
  border: none;
  min-height: 84px;
  padding: 0.72rem 0.45rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.2rem;
  text-align: center;
  border-radius: 0;
  background: rgba(12, 16, 21, 0.48);
  color: rgba(218, 225, 233, 0.56);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease,
    background-color 0.2s ease, background-image 0.2s ease, color 0.2s ease;
  box-shadow: none;
  cursor: pointer;

  &:hover,
  &:focus-visible,
  &.is-active {
    z-index: 2;
    background: var(--card-active-background);
    color: var(--card-active-color);
    box-shadow: inset 0 0 0 1px var(--card-active-ring);
  }

  &:focus-visible {
    outline: 1px solid var(--card-active-ring);
    outline-offset: -1px;
  }
}

.formations-menu__stack--left .formations-menu__card {
  align-items: flex-start;
  padding-right: 3.15rem;
}

.formations-menu__stack--right .formations-menu__card {
  align-items: flex-end;
  padding-left: 3.15rem;
}

.formations-menu__card--taxi {
  --card-active-background: #002a54;
  --card-active-color: #feb914;
  --card-active-icon: #feb914;
  --card-active-ring: rgba(254, 185, 20, 0.3);
}

.formations-menu__card--vtc {
  --card-active-background: #002a54;
  --card-active-color: #feb914;
  --card-active-icon: #feb914;
  --card-active-ring: rgba(254, 185, 20, 0.3);
}

.formations-menu__card--modulable {
  --card-active-background: #feb914;
  --card-active-color: #002a54;
  --card-active-icon: #002a54;
  --card-active-ring: rgba(0, 42, 84, 0.16);
}

.formations-menu__card--mobilite {
  --card-active-background: #feb914;
  --card-active-color: #002a54;
  --card-active-icon: #002a54;
  --card-active-ring: rgba(0, 42, 84, 0.16);
}

.formations-menu__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  min-height: 42px;
  font-size: 1.45rem;
  line-height: 1;
  color: rgba(210, 218, 227, 0.5);
}

.formations-menu__card:hover .formations-menu__icon,
.formations-menu__card:focus-visible .formations-menu__icon,
.formations-menu__card.is-active .formations-menu__icon {
  color: var(--card-active-icon);
}

.formations-menu__label {
  display: none;
}

.formations-menu__center {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 4;
  transform: translate(-50%, -50%);
  width: 104px;
  height: 104px;
  border: 4px solid transparent;
  border-radius: 50%;
  background: radial-gradient(
    circle at 32% 28%,
    #4d5560 0%,
    #181e26 45%,
    #04070b 100%
  );
  box-shadow: 0 10px 18px rgba(0, 42, 84, 0.14),
    inset 0 5px 10px rgba(255, 255, 255, 0.12),
    inset 0 -6px 12px rgba(0, 0, 0, 0.42);
  display: flex;
  justify-content: center;
  align-items: center;
  background-clip: padding-box;
  padding: 0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  &:disabled {
    cursor: default;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.03);
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.78);
    outline-offset: 2px;
  }
}

.formations-menu__center.is-idle .formations-menu__dial-readout {
  strong {
    font-size: 0.86rem;
  }

  span {
    max-width: 80px;
    line-height: 1.16;
    letter-spacing: 0.08em;
  }
}

.formations-menu__dial-track {
  position: absolute;
  inset: 10px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-bottom-color: transparent;
  background: radial-gradient(
    circle at 50% 62%,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.02) 58%,
    rgba(0, 0, 0, 0.32) 100%
  );
}

.formations-menu__dial-tick {
  position: absolute;
  left: 50%;
  top: 10px;
  width: 2px;
  height: 11px;
  border-radius: 999px;
  background: rgba(255, 248, 234, 0.4);
  transform-origin: center 39px;

  &.is-hot {
    background: rgba(185, 18, 18, 0.95);
  }
}

.formations-menu__dial-needle {
  position: absolute;
  inset: 0;
  transform: rotate(var(--needle-angle, -112deg));
  transition: transform 0.52s cubic-bezier(0.22, 1, 0.36, 1);
}

.formations-menu__dial-needle-arm {
  position: absolute;
  left: 50%;
  top: 15px;
  width: 3px;
  height: 35px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ff4036 0%, #7b0909 100%);
  transform: translateX(-50%);
  transform-origin: center bottom;
  box-shadow: 0 0 10px rgba(192, 14, 14, 0.18);
}

.formations-menu__dial-cap {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid rgba(255, 248, 234, 0.95);
  background: linear-gradient(180deg, #002a54 0%, #021a31 100%);
  transform: translate(-50%, -50%);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.24);
}

.formations-menu__dial-readout {
  position: absolute;
  bottom: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;

  strong {
    font-family: "cunia";
    font-size: 0.72rem;
    font-weight: normal;
    letter-spacing: 0.08em;
    color: #feb914;
  }

  span {
    margin-top: 0.14rem;
    font-family: "adam";
    font-size: 0.34rem;
    letter-spacing: 0.18em;
    color: #c63d34;
  }
}

.formations-menu__dial-gear {
  position: absolute;
  top: 17px;
  right: 17px;
  min-width: 16px;
  min-height: 16px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 248, 234, 0.12);
  color: $color-text-light;
  font-family: "cunia";
  font-size: 0.5rem;
  line-height: 1;
}

.formations-menu__paddle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: clamp(38px, 10vw, 46px);
  min-height: 172px;
  padding: 0.4rem 0.2rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: linear-gradient(
    180deg,
    #7b8188 0%,
    #424952 10%,
    #181d24 28%,
    #05070a 100%
  );
  color: $color-text-light;
  box-shadow: 0 10px 18px rgba(0, 42, 84, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 255, 255, 0.28);
    box-shadow: 0 12px 22px rgba(0, 42, 84, 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.16);
  }

  &:focus-visible {
    outline: 3px solid rgba(254, 185, 20, 0.8);
    outline-offset: 4px;
  }
}

.formations-menu__paddle-mark {
  display: block;
  font-family: "Spartan", "Arial Black", Arial, sans-serif;
  font-weight: 700;
  font-size: 1.3rem;
  line-height: 1;
}

.formations-menu__paddle-text {
  display: none;
}

.formations-menu__paddle--left {
  border-radius: 18px 0 0 18px;
}

.formations-menu__paddle--right {
  border-radius: 0 18px 18px 0;
}

.toggle-content {
  width: 100%;
  margin-top: 0px;
}

.toggle-overview {
  width: min(100%, 1180px);
  margin: 0 auto;
  padding: 8px 16px clamp(84px, 10vw, 122px);

  &__hero {
    width: min(100%, 920px);
    margin: 0 auto 34px;
    padding: clamp(24px, 4vw, 38px) clamp(22px, 4.5vw, 42px);
    text-align: center;
    border-radius: 30px;
    background: #ffffff;
    box-shadow: 0 22px 48px rgba(0, 42, 84, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.94);

    &::before {
      content: "";
      display: block;
      width: 92px;
      height: 5px;
      margin: 0 auto 18px;
      border-radius: 999px;
      background: $color-secondary;
      box-shadow: 0 10px 18px rgba(254, 185, 20, 0.18);
    }
  }

  &__eyebrow {
    margin: 0 0 12px;
    color: $color-secondary;
    font-family: "adam";
    font-size: 0.78rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  &__title {
    margin: 0;
    color: $color-primary;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.44rem, 1.14rem + 0.88vw, 2.1rem);
    line-height: 1.04;
    letter-spacing: 0.02em;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.88),
      0 14px 28px rgba(0, 42, 84, 0.08);
    text-wrap: balance;
  }

  &__copy {
    margin: 16px auto 0;
    max-width: 780px;
    color: rgba(43, 43, 43, 0.88);
    font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
    font-size: clamp(1rem, 0.96rem + 0.18vw, 1.08rem);
    line-height: 1.78;
    letter-spacing: 0.01em;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    width: min(100%, 980px);
    margin: 0 auto 30px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    background: rgba(0, 42, 84, 0.06);
    color: $color-primary;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: inset 0 0 0 1px rgba(0, 42, 84, 0.08);
    transition: background-color 0.2s ease, color 0.2s ease,
      box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      background: rgba(0, 42, 84, 0.1);
    }

    &:focus-visible {
      outline: 2px solid rgba(254, 185, 20, 0.7);
      outline-offset: 2px;
    }
  }

  &__chip--active {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 10px 18px rgba(0, 42, 84, 0.14);
  }

  &__blocks {
    width: min(100%, 1120px);
    margin: 0 auto;

    > .hr-d {
      width: 94px;
      height: 4px;
      margin: 34px auto 38px;
      border-radius: 999px;
      background: $color-secondary;
      opacity: 1;
      box-shadow: 0 10px 22px rgba(254, 185, 20, 0.18);
    }
  }
}

.toggle-item {
  display: none;
}

.toggle-item.active {
  display: block;

  :deep(.formation > h2) {
    display: block;
    width: 100%;
    margin: 0 auto 28px;
    padding: 12px 18px;
    border-radius: 18px;
    background: $color-primary;
    color: $color-secondary;
    text-align: center;
    font-size: clamp(1.34rem, 1.08rem + 0.5vw, 1.72rem);
    line-height: 1.1;
    letter-spacing: 0.05em;
    box-shadow: 0 10px 22px rgba(0, 42, 84, 0.14);
  }
}

@media screen and (min-width: 992px) {
  .formations-menu {
    --desktop-card-height: 152px;
    --desktop-paddle-width: clamp(78px, 6vw, 108px);
    gap: 6px;
    width: min(100%, 1420px);
    margin: 20px auto 16px;
    padding-inline: 18px;
    padding-bottom: 14px;
  }

  .formations-menu__cluster {
    display: flex;
    align-items: stretch;
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    min-height: 0;
    gap: 0;
  }

  .formations-menu__stack {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    gap: 0;
  }

  .formations-menu__stack--left {
    border-right: none;
  }

  .formations-menu__stack .formations-menu__card:first-child {
    border-bottom: none;
  }

  .formations-menu__card {
    flex: 1 1 0;
    min-height: var(--desktop-card-height);
    height: var(--desktop-card-height);
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0.72rem 1rem;
    gap: 0.72rem;
    border-radius: 0;
  }

  .formations-menu__stack--left .formations-menu__card,
  .formations-menu__stack--right .formations-menu__card {
    align-items: center;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .formations-menu__icon {
    min-width: 44px;
    min-height: 44px;
    font-size: 1.7rem;
    flex: 0 0 44px;
  }

  .formations-menu__label {
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
    align-items: flex-start;
    text-align: left;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    line-height: 1.02;

    strong {
      font-weight: 700;
      font-size: 0.9rem;
    }

    span {
      font-size: 0.84rem;
    }
  }

  .formations-menu__card--vtc {
    padding-right: 4.2rem;
    justify-content: flex-start;
    text-align: left;
  }

  .formations-menu__card--modulable {
    padding-left: 4.2rem;
    flex-direction: row-reverse;
    justify-content: flex-start;
    text-align: right;
  }

  .formations-menu__card--mobilite {
    flex-direction: row-reverse;
  }

  .formations-menu__card--modulable .formations-menu__label,
  .formations-menu__card--mobilite .formations-menu__label {
    align-items: flex-end;
    text-align: right;
  }

  .formations-menu__center {
    width: 128px;
    height: 128px;
    border-width: 3px;
    box-shadow: 0 12px 20px rgba(0, 42, 84, 0.14),
      inset 0 5px 10px rgba(255, 255, 255, 0.12),
      inset 0 -6px 12px rgba(0, 0, 0, 0.42);
  }

  .formations-menu__dial-track {
    inset: 12px;
  }

  .formations-menu__dial-tick {
    top: 11px;
    height: 14px;
    transform-origin: center 49px;
  }

  .formations-menu__dial-needle-arm {
    top: 17px;
    height: 45px;
  }

  .formations-menu__dial-readout {
    bottom: 22px;

    strong {
      font-size: 0.94rem;
    }

    span {
      font-size: 0.4rem;
    }
  }

  .formations-menu__dial-gear {
    top: 22px;
    right: 22px;
  }

  .formations-menu__paddle {
    width: var(--desktop-paddle-width);
    min-height: var(--desktop-card-height);
    height: var(--desktop-card-height);
    padding: 0.72rem 0.5rem;
  }

  .formations-menu__paddle-mark {
    font-size: 1.45rem;
  }

  .formations-menu__paddle-text {
    display: block;
    margin-top: 0.12rem;
    font-family: "adam";
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .formations-menu__paddle--left {
    border-radius: 30px 0 0 30px;
  }

  .formations-menu__paddle--right {
    border-radius: 0 30px 30px 0;
  }

  .toggle-overview {
    padding-inline: 22px;
    padding-bottom: clamp(92px, 9vw, 132px);
  }

  .toggle-overview__hero {
    margin-bottom: 40px;
  }

  .toggle-overview__chips {
    margin-bottom: 42px;
  }
}

.pop.active {
  @include fade-in(1.2s);
}
</style>
